import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Trophy, ChevronLeft, RefreshCw, BarChart3, Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import ShareButtons from "@/components/ShareButtons";
import CelebrateShareBanner from "@/components/CelebrateShareBanner";
import PastSelfDelta from "@/components/PastSelfDelta";
import { MockResultSkeleton } from "@/components/skeletons";
import { bigCelebrate, streakBurst } from "@/lib/confetti";
import { webAppUrl } from "@/lib/platform";

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export default function MockExamResults() {
  const { sessionId } = useParams();
  const [results, setResults] = useState(null);
  const [celebrated, setCelebrated] = useState(false);

  useEffect(() => {
    api.get(`/student/mock-exam/${sessionId}`).then((r) => setResults(r.data)).catch(() => {});
  }, [sessionId]);

  // Fire celebration once when a pass score lands.
  useEffect(() => {
    if (results && results.passed && !celebrated) {
      setCelebrated(true);
      try { bigCelebrate(); } catch { /* confetti is non-critical */ }
    } else if (results && !results.passed && results.score_percentage >= 60 && !celebrated) {
      setCelebrated(true);
      try { streakBurst(); } catch { /* confetti is non-critical */ }
    }
  }, [results, celebrated]);

  if (!results) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <MockResultSkeleton />
      </div>
    );
  }

  const passed = !!results.passed;
  const palette = passed
    ? { ring: "ring-emerald-200", bg: "from-emerald-50 to-teal-50", num: "text-emerald-700", chip: "bg-emerald-100 text-emerald-800 border-emerald-300" }
    : { ring: "ring-rose-200", bg: "from-rose-50 to-orange-50", num: "text-rose-700", chip: "bg-rose-100 text-rose-800 border-rose-300" };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="mock-exam-results">
      <Link to="/student/mock-exam" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to Mock Exams
      </Link>

      <Card className={`p-8 border-slate-200 bg-gradient-to-br ${palette.bg}`} data-testid="mock-exam-result-hero">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5" /> Mock Exam Result
            </div>
            <h1 className={`font-display font-extrabold text-3xl mt-2 ${palette.num}`}>
              {passed ? "🎉 You passed!" : "Not quite — keep grinding"}
            </h1>
            <p className="text-slate-700 mt-2 text-sm">
              You scored <strong>{results.score_count}</strong> out of <strong>{results.length}</strong> ·
              {" "}Passing mark <strong>{results.passing_percent}%</strong>
            </p>
            <p className="text-slate-500 mt-1 text-xs">
              Finished in {formatDuration(results.duration_seconds)} · {results.length}-question exam
            </p>
          </div>
          <div className={`relative grid place-items-center min-w-[120px] h-[120px] rounded-2xl bg-white shadow-sm ring-4 ${palette.ring}`} data-testid="mock-exam-result-score-badge">
            <div className="text-center px-2">
              <div className={`font-display font-extrabold text-4xl leading-none tabular-nums ${palette.num}`}>
                {results.accuracy_percentage}%
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mt-1">
                Accuracy
              </div>
            </div>
          </div>
        </div>
        <Badge className={`mt-4 ${palette.chip}`} data-testid="mock-exam-result-status-badge">
          {passed ? "PASSED" : "FAILED"}
        </Badge>

        {results.status === "completed" && (
          <div className="mt-5 pt-4 border-t border-slate-200" data-testid="mock-exam-share-section">
            {results.accuracy_percentage >= 90 && (
              <CelebrateShareBanner
                title="🎉 Excellent score — share this win!"
                subtitle="A 90%+ mock-exam result is rare. Inspire your friends — your share gets viral CTAs working for you."
                testId="celebrate-mock-exam-share"
              />
            )}
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Share your result
            </div>
            <ShareButtons
              getShareUrl={async () => {
                const r = await api.post(`/student/share/mock-exam/${sessionId}`);
                return {
                  url: `${webAppUrl()}/share/mock-exam/${r.data.share_token}`,
                  newly_awarded_badge: r.data.newly_awarded_badge,
                };
              }}
              defaultText={`I just ${passed ? "passed" : "took"} a mock board exam on PrepMaster — ${Math.round(results.accuracy_percentage)}%! 🎯`}
              testIdPrefix="mock-exam-share"
            />
          </div>
        )}
      </Card>

      <PastSelfDelta />

      {results.per_subject?.length > 0 && (
        <Card className="p-6 border-slate-200" data-testid="mock-exam-subject-breakdown">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-600" /> Per-Subject Breakdown
          </h3>
          <div className="space-y-3">
            {results.per_subject.map((s) => (
              <div key={s.subject_id || s.subject_name}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-800">{s.subject_name}</span>
                  <span className="text-slate-500 tabular-nums">{s.correct}/{s.total} · {s.accuracy}%</span>
                </div>
                <Progress value={s.accuracy} className="h-2 mt-1" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {results.sampling_breakdown?.length > 0 && (
        <Card className="p-6 border-slate-200 bg-slate-50/50" data-testid="mock-exam-sampling-breakdown">
          <h3 className="font-display font-bold text-base mb-1 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-600" /> How this exam was composed
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            Questions were sampled <strong>proportionally</strong> from each subject in your course, mirroring the real board's mix.
          </p>
          <div className="space-y-2">
            {results.sampling_breakdown.map((b) => (
              <div key={b.subject_id || b.subject_name} className="flex items-center justify-between text-sm" data-testid={`sampling-row-${b.subject_id || b.subject_name}`}>
                <span className="font-medium text-slate-800 truncate pr-3">{b.subject_name}</span>
                <span className="text-slate-500 tabular-nums text-xs">
                  <strong className="text-slate-900">{b.sampled}</strong> sampled
                  <span className="text-slate-400"> · pool of {b.pool_total}</span>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {results.wrong_review?.length > 0 && (
        <Card className="p-6 border-slate-200" data-testid="mock-exam-wrong-review">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <XCircle className="w-4 h-4 text-rose-600" /> Review Wrong Answers ({results.wrong_review.length})
          </h3>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
            {results.wrong_review.map((w, i) => (
              <div key={w.question_id} className="p-4 rounded-md border border-slate-200 bg-slate-50" data-testid={`mock-exam-wrong-${i}`}>
                <div className="text-[11px] font-bold uppercase tracking-wider text-rose-600 mb-1">{w.subject_name}</div>
                <p className="text-sm text-slate-900 font-medium">{w.question_text}</p>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-3.5 h-3.5 mt-0.5 text-rose-500 shrink-0" />
                    <span className="text-rose-700">
                      <strong>Your answer:</strong> {w.selected_text || <em className="text-slate-500">No answer</em>}
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
                    <span className="text-emerald-800"><strong>Correct:</strong> {w.correct_text}</span>
                  </div>
                </div>
                {w.explanation?.correct_answer_explanation && (
                  <div className="mt-3 p-3 rounded-md bg-indigo-50 border border-indigo-200 text-[13px] text-slate-700">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 flex items-center gap-1 mb-1">
                      <Sparkles className="w-3 h-3" /> Rationale
                    </div>
                    {w.explanation.correct_answer_explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex gap-2 flex-wrap">
        <Link to="/student/mock-exam">
          <Button className="bg-amber-600 hover:bg-amber-700" data-testid="mock-exam-retake-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Take Another Mock Exam
          </Button>
        </Link>
        <Link to="/student/smart-review">
          <Button variant="outline" data-testid="mock-exam-go-smart-review-btn">
            Continue with Smart Review
          </Button>
        </Link>
      </div>
    </div>
  );
}
